<template>
  <div ref="pageRoot" class="alarm-analysis" :class="{ 'is-panel-open': insightPanelVisible }">
    <div class="content">
      <div ref="filterBarRef">
        <AlarmAnalysisFilterBar v-model:station-id="filterForm.stationId" v-model:period="period"
          :station-list="stationList" @query="refreshData" />
      </div>

      <div v-if="aiState !== 'idle' && !insightPanelVisible" class="xiaoqi-entry">
        <Transition name="xiaoqi-card">
          <div v-if="showXiaoqiCard" class="xiaoqi-card">
            <span class="xiaoqi-card__close mdi mdi-close" @click.stop="dismissCard" />
            <div class="xiaoqi-card__arrow" />
            <template v-if="aiState === 'loading'">
              <div class="xiaoqi-card__title">小奇正在思考中</div>
              <div class="xiaoqi-card__desc">正在翻阅数据，稍等一下...</div>
              <div class="xiaoqi-card__dots"><span /><span /><span /></div>
            </template>
            <template v-else-if="aiState === 'done'">
              <div class="xiaoqi-card__title">小奇看完图表啦</div>
              <div class="xiaoqi-card__desc">{{ visibleAiInsights[0]?.headline }}</div>
              <div class="xiaoqi-card__bottom">
                <span class="xiaoqi-card__action" @click.stop="insightPanelVisible = true">看看小奇怎么说 →</span>
                <span class="xiaoqi-card__never" @click.stop="dismissCardForever">不再提示</span>
              </div>
            </template>
            <template v-else-if="aiState === 'error'">
              <div class="xiaoqi-card__title">小奇走神了</div>
              <div class="xiaoqi-card__desc">分析遇到了点问题</div>
              <div class="xiaoqi-card__bottom">
                <span class="xiaoqi-card__action" @click.stop="startAiAnalysis">再试一次</span>
                <span class="xiaoqi-card__never" @click.stop="dismissCardForever">不再提示</span>
              </div>
            </template>
          </div>
        </Transition>
        <div class="xiaoqi-avatar" :class="{ 'is-analyzing': aiState === 'loading' }"
          @click="insightPanelVisible = true">
          <img src="@/assets/images/xiaoqi-avatar.png" alt="打开小奇分析" />
          <div v-if="aiState === 'loading'" class="xiaoqi-avatar__ring" />
        </div>
      </div>

      <AlarmAnalysisStats :stats="stats" />
      <AlarmAnalysisCharts ref="chartsRef" :is-multi-station="isMultiStation" :station-name="selectedStationName"
        :trend-source="trendSource"
        :hourly-source="hourlySource" :top-alarm-source="topAlarmSource" :device-type-source="deviceTypeSource"
        :eblock-source="eblockSource" :station-rank-source="stationRankSource" :region-source="regionSource"
        :station-device-source="stationDeviceSource" :loading="cardLoading" />
    </div>

    <Transition name="insight-panel">
      <AlarmInsightPanel ref="insightPanelRef" v-if="insightPanelVisible" :ai-state="aiState" :insights="visibleAiInsights"
        :scope-label="aiScopeLabel || scopeLabel" :error-message="aiErrorMessage" :pinned="insightPanelPinned"
        @close="insightPanelVisible = false" @toggle-pin="toggleInsightPanelPin" @retry="startAiAnalysis"
        @go-station="goToStation" @focus-chart="focusChart" />
    </Transition>
  </div>
</template>

<script setup>
import { ref, reactive, computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import dayjs from 'dayjs'
import useUserStore from '@/store/modules/user'
import useAppStore from '@/store/modules/app'
import {
  getAlarmAnalysisContext,
  getAlarmSummary,
  getAlarmTrend,
  getAlarmHourDistribution,
  getHighFrequencyAlarm,
  getDeviceAlarmDistribution,
  getEblockModelAlarm,
  getStationAlarmRank,
  getRegionAlarmAnalysis,
  getStationDeviceAlarm,
} from '@/api/alarm'
import { consumeAlarmInsights } from '@/api/alarmInsights'
import AlarmInsightPanel from './AlarmInsightPanel.vue'
import AlarmAnalysisFilterBar from './AlarmAnalysisFilterBar.vue'
import AlarmAnalysisStats from './AlarmAnalysisStats.vue'
import AlarmAnalysisCharts from './AlarmAnalysisCharts.vue'

const filterForm = ref({ stationId: null })
const period = ref('7')
const userStore = useUserStore()
const appStore = useAppStore()
const stationList = computed(() => appStore.stations.map(station => ({
  id: station.stationId,
  name: station.name || station.stationName || String(station.stationId),
})))

const trendSource = ref([])
const hourlySource = ref([])
const topAlarmSource = ref([])
const deviceTypeSource = ref([])
const eblockSource = ref([])
const stationRankSource = ref([])
const regionSource = ref([])
const stationDeviceSource = ref([])
const summarySource = ref(null)
const cardLoading = reactive({
  trend: false,
  hourly: false,
  topAlarms: false,
  deviceTypes: false,
  eblockModels: false,
  stations: false,
  regions: false,
  stationDevices: false,
})

const aiState = ref('idle')
const aiInsights = ref([])
const aiScopeLabel = ref('')
const aiErrorMessage = ref('')
const analysisContext = ref(null)
const pageRoot = ref(null)
const filterBarRef = ref(null)
const chartsRef = ref(null)
const insightPanelRef = ref(null)

let insightController = null
let insightRunSequence = 0
let dataRequestSequence = 0

const isMultiStation = computed(() => !filterForm.value.stationId)
const toCount = value => Number(value) || 0
const stats = computed(() => {
  const data = summarySource.value
  if (!data) return { total: 0, safetyCount: 0, incomeCount: 0, normalCount: 0, stationCount: 0 }
  return {
    total: toCount(data.totalCount),
    safetyCount: toCount(data.safetyCount),
    incomeCount: toCount(data.incomeCount),
    normalCount: toCount(data.normalCount),
    stationCount: toCount(data.stationCount),
  }
})

const insightChartMeta = {
  trend: { key: 'trend', title: '告警趋势' },
  hourly: { key: 'hourly', title: '时段热力' },
  topAlarms: { key: 'topAlarms', title: '高频告警' },
  deviceTypes: { key: 'deviceTypes', title: '设备类型分布' },
  eblockModels: { key: 'eblockModels', title: 'eBlock 型号对比' },
  stations: { key: 'stations', title: '电站告警排名' },
  stationDevices: { key: 'stationDevices', title: '设备告警统计' },
  regions: { key: 'regions', title: '地域分布' },
}
const insightChartAliases = new Map([
  ['trend', 'trend'], ['alarmTrend', 'trend'],
  ['hourly', 'hourly'], ['alarmHourDistribution', 'hourly'], ['hour-distribution', 'hourly'],
  ['topAlarms', 'topAlarms'], ['highFrequencyAlarm', 'topAlarms'], ['top-alarms', 'topAlarms'],
  ['deviceTypes', 'deviceTypes'], ['deviceAlarmDistribution', 'deviceTypes'],
  ['deviceTypeDistribution', 'deviceTypes'], ['equipment-distribution', 'deviceTypes'],
  ['stationDeviceTypes', 'deviceTypes'],
  ['eblockModels', 'eblockModels'], ['eblockModelAlarm', 'eblockModels'], ['eblock-models', 'eblockModels'],
  ['stations', 'stations'], ['stationAlarmRank', 'stations'], ['station-ranking', 'stations'],
  ['stationDevices', 'stationDevices'], ['stationDeviceAlarm', 'stationDevices'],
  ['station-device-alarm', 'stationDevices'],
  ['regions', 'regions'], ['regionAlarmAnalysis', 'regions'], ['region-distribution', 'regions'],
])
const multiStationOnlyInsightKeys = new Set(['eblockModels', 'stations', 'regions'])

function normalizeInsightCards(cards) {
  return cards.map(insight => {
    const order = Number(insight?.order)
    const title = String(insight?.chartTitle || '')
    let metaKey = insightChartAliases.get(insight?.chartKey)

    // 04 is always the device-type chart in both station scopes.
    if (order === 4 || /设备类型.*分布/.test(title)) metaKey = 'deviceTypes'
    if (!metaKey && order === 5 && !isMultiStation.value) metaKey = 'stationDevices'

    if (!metaKey || !insightChartMeta[metaKey]) return insight
    return { ...insight, chartKey: insightChartMeta[metaKey].key, chartTitle: insightChartMeta[metaKey].title }
  })
}

const visibleAiInsights = computed(() => isMultiStation.value
  ? aiInsights.value
  : aiInsights.value.filter(insight => !multiStationOnlyInsightKeys.has(insight.chartKey)))
const selectedStationName = computed(() => stationList.value
  .find(station => String(station.id) === String(filterForm.value.stationId))?.name || '当前电站')
const scopeLabel = computed(() => {
  const stationLabel = isMultiStation.value ? '全部电站' : selectedStationName.value
  return `${stationLabel} · 近${period.value}天`
})

const XIAOQI_PANEL_PINNED_KEY = 'emind_alarm_analysis_xiaoqi_panel_pinned'
const XIAOQI_NEVER_KEY = 'emind_alarm_analysis_xiaoqi_card_never'
const insightPanelPinned = ref(localStorage.getItem(XIAOQI_PANEL_PINNED_KEY) === '1')
const insightPanelVisible = ref(insightPanelPinned.value)
const cardDismissed = ref(false)
const neverShowCard = ref(localStorage.getItem(XIAOQI_NEVER_KEY) === '1')
const showXiaoqiCard = computed(() => aiState.value !== 'idle' && !cardDismissed.value && !neverShowCard.value)

function dismissCard() {
  cardDismissed.value = true
}

function dismissCardForever() {
  neverShowCard.value = true
  cardDismissed.value = true
  localStorage.setItem(XIAOQI_NEVER_KEY, '1')
}

function toggleInsightPanelPin() {
  insightPanelPinned.value = !insightPanelPinned.value
  if (insightPanelPinned.value) {
    insightPanelVisible.value = true
    localStorage.setItem(XIAOQI_PANEL_PINNED_KEY, '1')
  } else {
    localStorage.removeItem(XIAOQI_PANEL_PINNED_KEY)
  }
}

function updateXiaoqiRight() {
  const rect = filterBarRef.value?.getBoundingClientRect()
  if (!rect || !pageRoot.value) return
  pageRoot.value.style.setProperty('--xiaoqi-right', `${Math.max(24, window.innerWidth - rect.right + 18)}px`)
}

function buildTimeRange() {
  const end = dayjs()
  const start = end.subtract(Number(period.value), 'day')
  return {
    startTime: start.format('YYYY-MM-DD HH:mm:ss'),
    endTime: end.format('YYYY-MM-DD HH:mm:ss'),
  }
}

function unwrapListResponse(res) {
  const data = res?.data ?? res
  return Array.isArray(data) ? data : []
}

async function loadSummaryData(params, sequence) {
  try {
    const res = await getAlarmSummary(params)
    if (sequence !== dataRequestSequence) return
    summarySource.value = res?.data ?? null
  } catch (error) {
    if (sequence !== dataRequestSequence) return
    summarySource.value = null
    console.error('[告警分析] 获取统计汇总失败：', error)
  }
}

async function loadCardData(key, label, requestTask, target, sequence) {
  cardLoading[key] = true
  try {
    const res = await requestTask()
    if (sequence !== dataRequestSequence) return
    target.value = unwrapListResponse(res)
  } catch (error) {
    if (sequence !== dataRequestSequence) return
    target.value = []
    console.error(`[告警分析] 获取${label}失败：`, error)
  } finally {
    if (sequence === dataRequestSequence) cardLoading[key] = false
  }
}

async function loadAnalysisContextForAi(params, sequence) {
  try {
    const res = await getAlarmAnalysisContext(params)
    if (sequence !== dataRequestSequence) return
    const context = res?.data ?? res
    if (!context || typeof context !== 'object') {
      throw new Error('告警分析上下文接口未返回有效数据')
    }
    analysisContext.value = context
    startAiAnalysis(context)
  } catch (error) {
    if (sequence !== dataRequestSequence) return
    console.error('[告警分析] 获取小奇分析上下文失败：', error)
    aiErrorMessage.value = error?.message || '当前数据分析遇到问题，请稍后重试。'
    aiState.value = 'error'
  }
}

async function refreshData() {
  const sequence = ++dataRequestSequence
  const params = buildTimeRange()
  if (filterForm.value.stationId) params.stationId = filterForm.value.stationId

  insightController?.abort()
  insightRunSequence++
  analysisContext.value = null
  aiState.value = 'loading'
  aiInsights.value = []
  aiScopeLabel.value = ''
  aiErrorMessage.value = ''
  cardDismissed.value = false

  trendSource.value = []
  hourlySource.value = []
  topAlarmSource.value = []
  deviceTypeSource.value = []
  eblockSource.value = []
  stationRankSource.value = []
  regionSource.value = []
  stationDeviceSource.value = []
  summarySource.value = null

  const tasks = [
    loadSummaryData(params, sequence),
    loadCardData('trend', '告警趋势', () => getAlarmTrend(params), trendSource, sequence),
    loadCardData('hourly', '告警时段分布', () => getAlarmHourDistribution(params), hourlySource, sequence),
    loadCardData('topAlarms', '高频告警统计', () => getHighFrequencyAlarm(params), topAlarmSource, sequence),
    loadCardData('deviceTypes', '设备告警分布', () => getDeviceAlarmDistribution(params), deviceTypeSource, sequence),
  ]

  if (isMultiStation.value) {
    tasks.push(
      loadCardData('eblockModels', 'eBlock 型号对比', () => getEblockModelAlarm(params), eblockSource, sequence),
      loadCardData('stations', '电站告警排名', () => getStationAlarmRank(params), stationRankSource, sequence),
      loadCardData('regions', '地域分析', () => getRegionAlarmAnalysis(params), regionSource, sequence),
    )
    cardLoading.stationDevices = false
  } else {
    tasks.push(
      loadCardData('stationDevices', '设备告警统计', () => getStationDeviceAlarm(params), stationDeviceSource, sequence),
    )
    cardLoading.eblockModels = false
    cardLoading.stations = false
    cardLoading.regions = false
  }

  tasks.push(loadAnalysisContextForAi(params, sequence))
  await Promise.allSettled(tasks)
}

function goToStation(stationId) {
  insightPanelRef.value?.scrollToTop()
  filterForm.value.stationId = stationId
  nextTick(refreshData)
}

function focusChart(chartKey) {
  chartsRef.value?.focusChart(chartKey)
}

async function startAiAnalysis(context) {
  insightController?.abort()
  insightController = new AbortController()
  const currentRun = ++insightRunSequence
  cardDismissed.value = false
  aiState.value = 'loading'
  aiInsights.value = []
  aiScopeLabel.value = ''
  aiErrorMessage.value = ''
  let resultReceived = false
  let failureReceived = false
  try {
    const ctx = context?.contextVersion ? context : analysisContext.value
    if (!ctx) throw new Error('缺少分析上下文，无法进行交集解读')
    console.log('[告警分析] POST /alarm-analysis/insights 请求体：', ctx)
    await consumeAlarmInsights(ctx, event => {
      if (currentRun !== insightRunSequence) return
      switch (event.type) {
        case 'analysis_started':
          aiState.value = 'loading'
          break
        case 'analysis_completed':
          aiInsights.value = normalizeInsightCards(
            Array.isArray(event.insights?.cards) ? event.insights.cards : [],
          )
          aiScopeLabel.value = event.insights?.scopeLabel || ''
          aiState.value = 'done'
          resultReceived = true
          break
        case 'analysis_failed':
          aiErrorMessage.value = event.message || '当前数据分析遇到问题，请稍后重试。'
          aiState.value = 'error'
          failureReceived = true
          console.error('[告警分析] SSE 分析失败：', event)
          break
        case 'done':
        case 'step_detail':
          break
        default:
          console.debug('[告警分析] 忽略未知 SSE 事件：', event)
      }
    }, insightController.signal, { userId: userStore.userId })
    if (currentRun !== insightRunSequence) return
    if (!resultReceived && !failureReceived) throw new Error('分析连接已结束，但未返回分析结果')
  } catch (error) {
    if (error?.name === 'AbortError' || currentRun !== insightRunSequence) return
    console.error('[告警分析] 交集解读失败：', error)
    aiErrorMessage.value = error?.message || '当前数据分析遇到问题，请稍后重试。'
    aiState.value = 'error'
  }
}

watch(insightPanelVisible, () => {
  nextTick(() => {
    updateXiaoqiRight()
    requestAnimationFrame(() => chartsRef.value?.resize())
  })
  window.setTimeout(() => {
    updateXiaoqiRight()
    chartsRef.value?.resize()
  }, 280)
})

onMounted(() => {
  if (!appStore.stations.length) appStore.getStations()
  nextTick(() => requestAnimationFrame(updateXiaoqiRight))
  refreshData()
  window.addEventListener('resize', updateXiaoqiRight)
})

onUnmounted(() => {
  dataRequestSequence++
  insightRunSequence++
  insightController?.abort()
  insightController = null
  window.removeEventListener('resize', updateXiaoqiRight)
})
</script>

<style scoped lang="scss">
.alarm-analysis {
  --primary: var(--color-primary, #2B57EE);
  --primary-light: color-mix(in srgb, var(--color-primary) 14%, #ffffff);
  --primary-faint: color-mix(in srgb, var(--color-primary) 8%, #f4f6fd);
  --cat-safety: #FF1E1E;
  --cat-income: #F59E0B;
  --cat-normal: #3A68FF;
  --card-bg: rgba(255, 255, 255, 0.82);
  --text-primary: var(--color-text, #1a2234);
  --text-secondary: color-mix(in srgb, var(--color-text, #1a2234) 78%, #7a8199);
  --text-muted: color-mix(in srgb, var(--color-text, #1a2234) 55%, #a0a6bd);
  --border: #e9efff;
  --surface-muted: #eef1fb;
  --shadow-card: 0 2px 8px rgba(8, 40, 87, 0.05);
  --chart-axis-label: color-mix(in srgb, var(--color-text, #1a2234) 72%, #7a8199);
  --chart-split-line: var(--color-td-border, #edf2f6);
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0;
  height: 100%;
  overflow: hidden;
  transition: grid-template-columns 0.26s ease, gap 0.26s ease;

  &.is-panel-open {
    grid-template-columns: minmax(0, 1fr) minmax(360px, 440px);
    gap: 8px;
  }
}

.content {
  min-width: 0;
  height: 100%;
  overflow-y: auto;
  scrollbar-gutter: stable;
}

.xiaoqi-entry {
  position: fixed;
  top: 186px;
  right: var(--xiaoqi-right, 42px);
  z-index: 30;
  width: 60px;
  height: 60px;
}

.xiaoqi-avatar {
  position: absolute;
  inset: 0;
  width: 60px;
  height: 60px;
  cursor: pointer;
  filter: drop-shadow(0 6px 16px rgba(43, 87, 238, 0.28));
  transition: transform 0.3s;
  animation: xiaoqi-float 3.5s ease-in-out infinite;

  &:hover { transform: scale(1.1); }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transform: scale(1.2);
  }

  &.is-analyzing { animation: xiaoqi-float 1.8s ease-in-out infinite; }

  &__ring {
    position: absolute;
    inset: -6px;
    border-radius: 50%;
    border: 2.5px solid transparent;
    border-top-color: var(--primary);
    border-right-color: color-mix(in srgb, var(--primary) 40%, transparent);
    animation: xiaoqi-spin 1.2s linear infinite;
  }
}

.xiaoqi-card {
  position: absolute;
  top: 50%;
  right: 74px;
  width: 264px;
  background: #fff;
  border-radius: 16px;
  padding: 16px 18px;
  box-shadow: 0 12px 40px rgba(8, 40, 87, 0.16), 0 4px 12px rgba(43, 87, 238, 0.08);
  cursor: pointer;
  transform: translateY(-50%);
  transition: box-shadow 0.2s;

  &:hover {
    transform: translateY(calc(-50% - 2px));
    box-shadow: 0 16px 48px rgba(8, 40, 87, 0.2), 0 6px 16px rgba(43, 87, 238, 0.12);
  }

  &__arrow {
    position: absolute;
    top: calc(50% - 6px);
    right: -6px;
    width: 12px;
    height: 12px;
    background: #fff;
    transform: rotate(45deg);
    box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.04);
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 6px;
  }

  &__desc {
    font-size: 12px;
    color: var(--text-muted);
    line-height: 1.5;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__action {
    font-size: 12px;
    font-weight: 500;
    color: var(--primary);
    cursor: pointer;
  }

  &__close {
    position: absolute;
    top: 8px;
    right: 10px;
    font-size: 14px;
    color: #bbb;
    cursor: pointer;
    transition: color 0.2s;

    &:hover { color: #666; }
  }

  &__bottom {
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__never {
    font-size: 11px;
    color: #bbb;
    cursor: pointer;
    transition: color 0.2s;

    &:hover { color: #999; }
  }

  &__dots {
    display: flex;
    gap: 4px;
    margin-top: 10px;

    span {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--primary);
      opacity: 0.4;
      animation: xiaoqi-dot-bounce 1.4s ease-in-out infinite;

      &:nth-child(2) { animation-delay: 0.2s; }
      &:nth-child(3) { animation-delay: 0.4s; }
    }
  }
}

.xiaoqi-card-enter-active { animation: xiaoqi-card-in 0.35s cubic-bezier(0.34, 1.4, 0.64, 1); }
.xiaoqi-card-leave-active { animation: xiaoqi-card-in 0.2s ease-in reverse; }

@keyframes xiaoqi-card-in {
  from { opacity: 0; transform: translateY(-50%) translateX(12px) scale(0.92); }
  to { opacity: 1; transform: translateY(-50%) translateX(0) scale(1); }
}

@keyframes xiaoqi-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

@keyframes xiaoqi-spin {
  to { transform: rotate(360deg); }
}

@keyframes xiaoqi-dot-bounce {
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1.1); }
}

.insight-panel-enter-active,
.insight-panel-leave-active {
  transition: opacity 0.24s ease, transform 0.24s ease;
}

.insight-panel-enter-from,
.insight-panel-leave-to {
  opacity: 0;
  transform: translateX(22px);
}

@media (max-width: 1280px) {
  .alarm-analysis.is-panel-open {
    grid-template-columns: minmax(0, 1fr) 360px;
  }
}

@media (max-width: 900px) {
  .alarm-analysis,
  .alarm-analysis.is-panel-open {
    display: block;
  }

  .alarm-analysis> :deep(.insight-panel) {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 25;
    width: min(400px, calc(100% - 20px));
  }

  .xiaoqi-card {
    display: none;
  }
}
</style>
